import Address from '@/classes/addresses/Address'
import Campaign from '@/classes/campaigns/Campaign'
import Benefiting from '@/classes/benefitings/Benefiting'
import Template from '@/classes/templates/Template'
import Procedures from '@/classes/procedures/Procedures'
import Users from '@/classes/users/Users'
import Statistics from '@/classes/statistics/Statistics'
import { get as _get, capitalize as _capitalize } from 'lodash'

export default class Project {
  constructor ({ id = null, name = null, currency = null, fixed_goal_amount = null, image = null, description = null, status = null, slug = null, address = {}, campaign = {}, procedures, users, owner_id, settings = {}, tags = [], informations = {}, video_url = null, invite_url = null, statistics = {}, benefiting = {}, amount_collected = 0, contributors_count = 0, template_id = null, template = {}, start_date = null, end_date = null, event_start_date = null, event_end_date = null, created_at = null } = {}) {
    this.id = id
    this.name = name
    this.currency = currency
    this.fixed_goal_amount = fixed_goal_amount
    this.image = image
    this.description = description
    this.status = status
    this.slug = slug
    this.address = new Address(_get(address, 'table', address))
    this.campaign = new Campaign(campaign)
    this.procedures = new Procedures(procedures)
    this.owner_id = owner_id
    this.users = new Users(users)
    this.settings = settings
    this.tags = tags
    this.informations = informations
    this.video_url = video_url
    this.invite_url = invite_url
    this.benefiting = new Benefiting(benefiting || {})
    this.template_id = template_id
    this.template = new Template(template)
    this.start_date = start_date
    this.end_date = end_date
    this.event_start_date = event_start_date
    this.event_end_date = event_end_date
    this.created_at = created_at
    this.statistics = new Statistics({ amount_collected, contributors_count, ...statistics })
  }

  get donation_procedure () {
    return this.procedures.donation
  }

  get goal_amount () {
    return this.donation_procedure?.goal_amount
  }

  get start_date () {
    return this._start_date || this.donation_procedure?.start_date
  }

  set start_date (start_date) {
    this._start_date = start_date
  }

  get end_date () {
    return this._end_date || this.donation_procedure?.end_date
  }

  set end_date (end_date) {
    this._end_date = end_date
  }

  get is_started () {
    return this.donation_procedure?.is_started
  }

  get is_ended () {
    return this.donation_procedure?.is_ended
  }

  get is_ongoing () {
    return this.donation_procedure?.is_ongoing
  }

  get limit_of_members () {
    return this.campaign.limit_of_members || 0
  }

  get goal_amount_per_member () {
    return this.campaign.goal_amount_per_member
  }

  get users_count () {
    return this.users.length
  }

  get members_count () {
    return this.users_count - 1
  }

  get members_left () {
    return this.limit_of_members - this.members_count
  }

  get is_goal_allowed () {
    return !('goal_amount' in this.campaign.project_display_settings) || !!this.campaign.project_display_settings.goal_amount
  }

  get is_event_allowed () {
    return this.campaign.project_display_settings.event?.is_project_member_edit_allowed ?? false
  }

  get is_duration_allowed () {
    return _get(this.campaign, 'project_display_settings.duration', true)
  }

  get show_event_time () {
    return this.campaign.project_display_settings.event?.show_time ?? true
  }

  get is_members_invite_allowed () {
    return !!this.campaign.project_display_settings.invite_project_members
  }

  get is_approved () {
    return this.status !== 'need_approval'
  }

  get to_general_settings () {
    return {
      name: this.name,
      image: this.image,
      video_url: this.video_url
    }
  }

  get to_database () {
    return {
      address: this.address,
      benefiting: this.benefiting.id,
      start_date: this.start_date,
      end_date: this.end_date,
      event_start_date: this.event_start_date,
      event_end_date: this.event_end_date,
      fixed_goal_amount: this.fixed_goal_amount,
      name: this.name,
      currency: this.currency
    }
  }

  get to_tamaro_automation () {
    return {
      koa_project_id: this.id,
      koa_project_name: this.name,
      koa_project_campaign_id: this.campaign.id,
      koa_project_owner_id: this.owner_id
    }
  }

  get to_mixpanel () {
    return {
      'Id': this.id,
      'Slug': this.slug,
      'Name': this.name,
      'Campaign Id': this.campaign?.id,
      'Status': _capitalize(this.status)
    }
  }

  get to_gtm () {
    return {
      project_id: this.id,
      project_slug: this.slug,
      project_name: this.name,
      project_status: this.status,
      campaign_id: this.campaign?.id
    }
  }

  isAuthorized (authUser) {
    return authUser && (this.users.some(user => user.id === authUser.id) || authUser.is_koalect || authUser.is_super_admin)
  }
}
